import type TreeModel from 'tree-model';
import type { TreeNode } from 'tree-model';

import { treeReducer } from '../../constants/reducer_ids';
import type { AppDispatch, GetState } from '../../store/types';
import type { AnyObject, ObjectWithId } from '../../types/common';
import { treeActions } from './actions';
import type { TreeReducerId, TreeReducerModel } from './types';
import { unfreezeChildren } from './utils';

export function clearTree(reducerId: TreeReducerId) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { treeModel } = getState()[treeReducer[reducerId]];
    const { treeRoot } = treeModel;
    treeRoot.drop();
    dispatch(treeActions(reducerId).clearTree());
  };
}

export type Branches = {
  objects: ObjectWithId[];
  data?: {
    total?: number;
    pages?: number;
    page?: number;
  };
  parent?: string;
  checkParentExpanded?: boolean;
  index?: number;
};

function attachBranches(
  tree: TreeModel<TreeReducerModel>,
  node: TreeNode<TreeReducerModel>,
  {
    objects,
    index,
    data = {},
    checkParentExpanded = false,
  }: Omit<Branches, 'parent'>,
) {
  unfreezeChildren(node);
  node.model = { ...node.model, ...data };
  const parentExpanded = checkParentExpanded && node.model.expanded;
  const hasLoadedChildren = node.children.length > 0;
  if (checkParentExpanded === false || parentExpanded || hasLoadedChildren) {
    const seenIds = new Set(node.children.map((child) => child.model.id));
    const isNewChild = ({ id }: ObjectWithId) =>
      Boolean(id) && seenIds.size < seenIds.add(id).size;
    objects.filter(isNewChild).forEach((o) => {
      const child = tree.parse(o);
      // add the elements to the tree just if the parent is expanded
      if (index === undefined) {
        node.addChild(child);
      } else {
        node.addChildAtIndex(child, index);
      }
    });
  }
}

export function addBranches(
  reducerId: TreeReducerId,
  { parent, ...branches }: Branches,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { treeModel } = getState()[treeReducer[reducerId]];
    const { tree, treeRoot } = treeModel;
    const node = parent
      ? treeRoot.first((n) => n.model.id === parent)
      : treeRoot;
    if (node) {
      attachBranches(tree, node, branches);
    }
    dispatch(treeActions(reducerId).addBranches(treeRoot));
  };
}

export function addBranchesToNode(
  reducerId: TreeReducerId,
  node: TreeNode<TreeReducerModel>,
  branches: Omit<Branches, 'parent'>,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { tree, treeRoot } = getState()[treeReducer[reducerId]].treeModel;
    attachBranches(tree, node, branches);
    dispatch(treeActions(reducerId).addBranches(treeRoot));
  };
}

export function modifyBranchByID(
  reducerId: TreeReducerId,
  id: string,
  data: AnyObject,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { treeRoot } = getState()[treeReducer[reducerId]].treeModel;
    const node = id ? treeRoot.first((n) => n.model.id === id) : treeRoot;
    if (node) {
      node.model = { ...node.model, ...data };
    }
    dispatch(treeActions(reducerId).modifyBranch(treeRoot));
  };
}

export function modifyBranchByNode(
  reducerId: TreeReducerId,
  node: TreeNode,
  data: AnyObject,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { treeModel } = getState()[treeReducer[reducerId]];
    const { treeRoot } = treeModel;
    const newNode = node;
    newNode.model = { ...node.model, ...data };
    dispatch(treeActions(reducerId).modifyBranch(treeRoot));
  };
}

export function removeBranchByID(reducerId: TreeReducerId, id: string) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { treeModel } = getState()[treeReducer[reducerId]];
    const { treeRoot } = treeModel;
    const node = treeRoot.first((n) => n.model.id === id);
    if (node) {
      if (node.parent) {
        unfreezeChildren(node.parent);
      }
      node.drop();
    }
    dispatch(treeActions(reducerId).removeBranch(treeRoot));
  };
}

export function setTreeSearchLoading(
  reducerId: TreeReducerId,
  loading: boolean,
) {
  return (dispatch: AppDispatch) => {
    dispatch(treeActions(reducerId).setTreeSearchLoading(loading));
  };
}

export function setTreeSearchQuery(reducerId: TreeReducerId, query: string) {
  return (dispatch: AppDispatch) => {
    dispatch(treeActions(reducerId).setTreeSearchQuery(query));
  };
}
